/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import { Logo } from "frontend/src/components/Logo"
import { CMSLink } from "frontend/src/components/CMSLink"
import { useState } from "react" // import { Fragment, useState } from "react"
import Headroom from "react-headroom"
import { Touchable } from "frontend/src/components/Touchable"
import { Icon } from "frontend/src/components/Icon"
import { useLocation } from "frontend/src/utils/useLocation"
import { useCart } from "frontend/src/utils/useCart"
import { Flex, SectionGrid } from "frontend/src/components/Layout"
import { useCallback } from "react"
import { useTrackingData } from "frontend/src/utils/PageContext"
import { FiShoppingCart } from "react-icons/fi"

const linkTextStyle = {
  textDecoration: "none",
  color: "almostBlack",
  textTransform: "uppercase",
  fontFamily: "heading",
  fontSize: 3,
  mx: 7,
  fontWeight: "regular",
  letterSpacing: "0px",
}

function CartButton() {
  const { numberOfItems, prices, toggle } = useCart()
  const pageTrackingData = useTrackingData()
  const trackClick = useCallback(() => {
    if (typeof window !== `undefined`) {
      window.dataLayer.push({
        event: "cartOpen",
        price: prices.finalPrice,
        section: "Menu",
        ...pageTrackingData,
      })
    }
  }, [pageTrackingData, prices])
  return (
    <Touchable
      onClick={() => {
        toggle()
        trackClick()
      }}
      sx={{ display: "flex", alignItems: "center", position: "relative", height: 19 }}
    >
      <div sx={{ ...linkTextStyle, display: ["none", "none", "none", "flex"], mr: 3 }}>
        Warenkorb
      </div>
      <FiShoppingCart size="22px" sx={{ mt: -2 }} color="black" />
      <Flex
        align="center"
        justify="center"
        sx={{
          position: "absolute",
          top: -3,
          right: -2,
          bg: "black",
          color: "white",
          borderRadius: 14,
          fontSize: "9px",
          pt: 1,
          width: 14,
          height: 14,
          pb: 1,
        }}
      >
        {numberOfItems}
      </Flex>
    </Touchable>
  )
}

function MobileMenu({ logo, links, withCart, className = "" }) {
  const [showMenu, setShowMenu] = useState(false)
  return (
    <Flex.row
      as="nav"
      className={className}
      align="center"
      justify="space-between"
      sx={{
        px: 6,
        height: 80,
        zIndex: 100,
        backgroundColor: "background",
        boxShadow: "0px 2px 24px #00000017",
        transition: "box-shadow 300ms ease-out 0s",
        MozTransition: "box-shadow 300ms ease-out 0s",
        WebkitTransition: "box-shadow 300ms ease-out 0s",
        OTransition: "box-shadow 300ms ease-out 0s",
      }}
    >
      <Touchable onClick={() => setShowMenu(s => !s)}>
        <Icon name={showMenu ? "Close" : "Menu"} size={16} />
      </Touchable>
      {logo && (
        <Touchable to="/">
          <Logo name={logo} height={28} forceColor="black" />
        </Touchable>
      )}
      <div>{withCart && <CartButton />}</div>
      <Flex
        sx={{
          opacity: showMenu ? 1 : 0,
          transformOrigin: "top",
          transform: showMenu ? "scaleY(1)" : "scaleY(0)",
          transition: "all 0.2s linear",
          pointerEvents: showMenu ? "auto" : "none",
          width: "100%",
          bg: "white",
          position: "absolute",
          top: 80,
          left: 0,
          zIndex: 200,
          boxShadow: "inset 0px 3px 12px -9px rgba(0,0,0,3)",
        }}
      >
        {links.map(link => {
          return (
            <CMSLink
              key={link.externalUrl || link.internalRef?.slug?.current}
              link={link}
              sx={{
                ...linkTextStyle,
                p: 6,
                fontSize: 3,
                borderTop: "1px #bbb solid",
              }}
            >
              {link.title}
            </CMSLink>
          )
        })}
      </Flex>
    </Flex.row>
  )
}

function DesktopMenu({ logo, links, withCart, className = "" }) {
  const { pathname } = useLocation()

  const defaultMenu = (
    <Flex.row
      sx={{
        flexShrink: 1,
      }}
    >
      {links.map(link => {
        const isToCurrent =
          link.internalRef && pathname.split("/")[1] === link.internalRef.slug.current
        return (
          <CMSLink
            key={link.externalUrl || link.internalRef?.slug?.current}
            link={link}
            sx={{
              ...linkTextStyle,
              mx: [0, 0, 7, 7],
              borderBottom: isToCurrent ? "1px solid black" : "none",
            }}
          >
            {link.title}
          </CMSLink>
        )
      })}
    </Flex.row>
  )

  return (
    <SectionGrid
      sx={{
        backgroundColor: "#ffffff",
        boxShadow: "0px 2px 24px #00000017",
        transition: "box-shadow 300ms ease-out 0s",
        MozTransition: "box-shadow 300ms ease-out 0s",
        WebkitTransition: "box-shadow 300ms ease-out 0s",
        OTransition: "box-shadow 300ms ease-out 0s",
      }}
    >
      <SectionGrid.wide
        as="nav"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 7,
          mx: 6,
        }}
        className={className}
      >
        {logo && (
          <Touchable to="/" sx={{ display: "flex", alignItems: "center" }}>
            <Logo name={logo} height={[0, 0, 42, 42]} forceColor="black" />
          </Touchable>
        )}
        {defaultMenu}
        <div>{withCart && <CartButton />}</div>
      </SectionGrid.wide>
    </SectionGrid>
  )
}

function MainMenu(props) {
  return (
    <Headroom
      style={{
        transition: "all 0.5s ease-in-out",
        WebkitTransition: "all 0.5s ease-in-out",
        MozTransition: "all 0.5s ease-in-out",
        OTransition: "all 0.5s ease-in-out",
      }}
      wrapperStyle={{
        position: "relative",
        zIndex: 100,
        opacity: 1,
      }}
    >
      <DesktopMenu sx={{ display: ["none", "none", "flex"] }} {...props} />
      <MobileMenu sx={{ display: ["flex", "flex", "none"] }} {...props} />
    </Headroom>
  )
}

export { MainMenu }
